import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

export interface ApiToken {
  id: string
  name: string
  last4: string
  scopes: string[]
  created_at: number
  expires_at: number | null
  last_used_at: number | null
  revoked_at: number | null
}

export interface CreateTokenBody {
  name: string
  scopes: string[]
  expires_in_seconds?: number | null
}

export interface CreateTokenResult {
  token: ApiToken
  raw: string
}

export function useTokens() {
  return useQuery({
    queryKey: ['tokens'],
    queryFn: () => apiFetch<ApiToken[]>('/api/tokens'),
  })
}

export function useCreateToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateTokenBody) =>
      apiFetch<CreateTokenResult>('/api/tokens', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  })
}

export function useRevokeToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token_id: string) =>
      apiFetch<void>(`/api/tokens/${token_id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  })
}

// Scope families exposed to the UI — mirrors ALL_SCOPES in src/auth/context.py.
export const ALL_SCOPES: { key: string; label: string }[] = [
  { key: 'mcp:search', label: 'MCP: search operation catalog' },
  { key: 'mcp:execute', label: 'MCP: execute operations' },
  { key: 'calls:read', label: 'Calls: read' },
  { key: 'calls:write', label: 'Calls: dial outbound / cancel' },
  { key: 'campaigns:read', label: 'Campaigns: read lists + contacts' },
  { key: 'campaigns:write', label: 'Campaigns: upload, update, delete' },
  { key: 'simulations:read', label: 'Simulations: read campaigns' },
  { key: 'simulations:write', label: 'Simulations: trigger test calls' },
  { key: 'contacts:read', label: 'Contacts: read' },
  { key: 'contacts:write', label: 'Contacts: create/update/delete' },
  { key: 'telemetry:read', label: 'Telemetry: read call history' },
  { key: 'usage:read', label: 'Usage: read billing + caps' },
]

export const DEFAULT_TOKEN_SCOPES = [
  'mcp:search',
  'mcp:execute',
  'calls:write',
  'campaigns:read',
  'simulations:write',
]
